/** Canonical resumable project upload protocol; web/lib/projectUploadProtocol.ts is a byte-pinned mirror. */
import { createRetryController, retryAfterMs, type RetryPolicy } from 'elowen-plugin-shared/retry';

import type { StoredAttachment } from './chatAttachments.js';

type UploadTarget = { sessionId?: string } | { projectId: number; directory: string };
interface UploadOptions { signal?: AbortSignal; onProgress?: (sent: number) => void }
interface UploadTransport {
  request: (path: string, init: RequestInit) => Promise<Response>;
  loadPolicy: (signal?: AbortSignal) => Promise<unknown>;
  getPolicy: () => RetryPolicy;
}

/** A refused upload, with the server's bounded explanation kept separate from transport errors. */
export class ProjectUploadRefusal extends Error {
  constructor(readonly reason?: string) { super(reason ?? 'Project upload failed'); this.name = 'ProjectUploadRefusal'; }
}

const MAX_REASON_CHARS = 300;
const sessionUrl = (id: string, suffix = ''): string => `/brain/uploads/sessions/${encodeURIComponent(id)}${suffix}`;

async function readRefusal(res: Response): Promise<{ received?: unknown; reason?: string }> {
  try {
    const body: unknown = await res.json();
    if (!body || typeof body !== 'object') return {};
    const error = 'error' in body ? body.error : undefined;
    return {
      received: 'received' in body ? body.received : undefined,
      reason: typeof error === 'string' && error.trim() ? error.trim().slice(0, MAX_REASON_CHARS) : undefined,
    };
  } catch (error) {
    // Proxies may refuse with HTML; a lost response body is still a transport failure.
    if (error instanceof SyntaxError) return {};
    throw error;
  }
}

/** Sequential Blob slices with durable offset recovery. Only the existing retry controller repeats parts;
 * opening and commit are never replayed. Cancellation disposal deliberately has no caller signal. */
export async function uploadProjectFileProtocol(
  file: Blob & { name: string }, target: UploadTarget, transport: UploadTransport, opts: UploadOptions = {},
): Promise<StoredAttachment> {
  const { onProgress, signal } = opts;
  let uploadId: string | undefined;
  try {
    await transport.loadPolicy(signal);
    signal?.throwIfAborted();
    const query = new URLSearchParams({ name: file.name || 'upload', size: String(file.size) });
    if ('projectId' in target) {
      query.set('projectId', String(target.projectId));
      query.set('directory', target.directory);
    } else if (target.sessionId) query.set('session', target.sessionId);
    const opened = await transport.request(`/brain/uploads/sessions?${query.toString()}`, { method: 'POST', signal });
    if (!opened.ok) throw new ProjectUploadRefusal((await readRefusal(opened)).reason);
    const begun = await opened.json() as { id?: unknown; partSize?: unknown };
    if (typeof begun.id !== 'string' || !begun.id) throw new ProjectUploadRefusal();
    const id = begun.id;
    uploadId = id;
    if (typeof begun.partSize !== 'number' || !Number.isSafeInteger(begun.partSize) || begun.partSize <= 0) {
      throw new ProjectUploadRefusal();
    }
    const partSize = begun.partSize;
    signal?.throwIfAborted();

    let sent = 0;
    while (sent < file.size) {
      signal?.throwIfAborted();
      const end = Math.min(sent + partSize, file.size);
      const bytes = file.slice(sent, end);
      let reason: string | undefined;
      const controller = createRetryController<number>({
        target: { kind: 'request', id: sessionUrl(id), ...('projectId' in target ? { projectId: target.projectId } : {}) },
        mode: 'request', getPolicy: transport.getPolicy, signal,
        attempt: async context => {
          let res: Response | undefined;
          try {
            // Exactly one request: restart recovery must not replay this attempt underneath its owner.
            res = await transport.request(`${sessionUrl(id)}?offset=${sent}`, {
              method: 'PUT', body: bytes,
              headers: { 'content-type': 'application/octet-stream' }, signal: context.signal,
            });
            const body: unknown = res.ok ? await res.json() : await readRefusal(res);
            if (!body || typeof body !== 'object') return { outcome: 'blocked', reason: 'closed' };
            const received = 'received' in body ? body.received : undefined;
            reason = 'reason' in body && typeof body.reason === 'string' ? body.reason : undefined;
            const eligible = res.ok || (res.status === 409 && received !== undefined) || res.status === 429 || res.status >= 500;
            if (!eligible) return { outcome: 'blocked', reason: res.status === 401 ? 'auth_required' : res.status === 403 ? 'forbidden' : 'closed' };
            if (received !== undefined) {
              if (typeof received !== 'number' || !Number.isSafeInteger(received) || received < sent || received > end) {
                return { outcome: 'blocked', reason: 'closed' };
              }
              // Only acknowledged durable progress earns a new policy budget.
              if (received > sent) return { outcome: 'succeeded', value: received };
            } else if (res.ok) return { outcome: 'blocked', reason: 'closed' };
            return { outcome: 'retry', reason: res.status === 429 ? 'rate_limited' : res.ok || res.status === 409 ? 'not_ready' : 'unavailable', retryAfterMs: retryAfterMs(res.headers.get('retry-after')) };
          } catch (error) {
            if (context.signal.aborted || !(error instanceof TypeError)
              || (res && !res.ok && res.status < 500 && res.status !== 429)) throw error;
            reason = undefined;
            return { outcome: 'retry', reason: 'network', retryAfterMs: retryAfterMs(res?.headers.get('retry-after')) };
          }
        },
      });
      try {
        const result = await controller.start().done;
        signal?.throwIfAborted();
        if (result.outcome !== 'succeeded') throw new ProjectUploadRefusal(reason);
        sent = result.value;
        onProgress?.(sent);
      } finally { await controller.stop(); }
    }
    signal?.throwIfAborted();
    const done = await transport.request(sessionUrl(id, '/commit'), { method: 'POST', signal });
    // A refused commit can leave a session open while a part is writing; it must still be disposed.
    if (!done.ok) throw new ProjectUploadRefusal((await readRefusal(done)).reason);
    uploadId = undefined;
    const body = await done.json() as Partial<StoredAttachment>;
    if (typeof body.path !== 'string' || typeof body.name !== 'string') throw new ProjectUploadRefusal();
    signal?.throwIfAborted();
    return { name: body.name, path: body.path, relative: typeof body.relative === 'string' ? body.relative : body.name };
  } catch (error) {
    if (uploadId) {
      try {
        const disposed = await transport.request(sessionUrl(uploadId), { method: 'DELETE', keepalive: true });
        if (!disposed.ok) throw new Error(`Project upload cleanup refused: HTTP ${disposed.status}`);
      } catch (cleanupError) {
        throw new AggregateError([error, cleanupError], 'Project upload and cleanup failed', { cause: error });
      }
    }
    throw error;
  }
}
